import { v4 as uuidv4 } from 'uuid'
import { DateTime, Duration } from 'luxon'
import type { Event as TServerEvent } from '@/api/client/models/Event'
import { Task } from '@/classes/Task'
import type { Site } from '@/api/client'
import { report } from '@/classes/manager/ExceptionReportingManager'

type TDehydratedEvent = {
  occured_at: string
  uuid: string
  id: number
  task_id: number
  project_id: number | null
  employee_id: number | null
  project_date_id: number | null
  task_label: string
  user_id: number | null
  transmitted: boolean
  duration_seconds: number
  version_id: number | null
  is_workplan_event: boolean | null
  is_deleted: boolean | null
  site: Site | null
  time_recording_action?: TServerEvent.time_recording_action
}

type TEventPayload = {
  id?: number
  uuid?: string | null
  occured_at: string
  employee_id?: number | null
  task_id: number
  task_label?: string | null
  project_id?: number | null
  project_date_id?: number | null
  version_id?: number | null
  is_workplan_event?: boolean | null
  is_deleted?: boolean | null
  site_id?: number | null
  time_recording_action?: TServerEvent.time_recording_action
}

class Event {
  occured_at: DateTime
  uuid: string
  id: number | null
  task_id: number
  project_id: number | null
  employee_id: number | null
  project_date_id: number | null
  task_label: string
  user_id: number | null
  transmitted: boolean
  duration_seconds: number
  version_id: number | null
  is_workplan_event: boolean | null
  is_deleted: boolean
  site: Site | null
  time_recording_action?: TServerEvent.time_recording_action

  constructor(
    task_id: number,
    project_id?: number | null,
    employee_id?: number | null,
    project_date_id?: number | null,
    occured_at?: DateTime,
    uuid?: string | null,
    id?: number | null,
    task_label?: string | null,
    user_id?: number | null,
    transmitted?: boolean,
    duration_seconds?: number,
    version_id?: number | null,
    is_workplan_event?: boolean | null,
    is_deleted?: boolean,
    site?: Site | null,
    time_recording_action?: TServerEvent.time_recording_action,
  ) {
    if (task_id === Task.id.PROJECT && !project_id) {
      throw new Error('Events with a PROJECT task must have a project_id')
    }
    if (!task_id) {
      throw new Error(
        'Cannot create Event without task_id' +
          JSON.stringify({
            task_id,
            project_id,
            employee_id,
            project_date_id,
            occured_at,
            uuid,
            id,
            task_label,
          }),
      )
    }
    if (!Object.values(Task.id).includes(task_id)) {
      report(new Error('Cannot create Event with this task_id: ' + task_id))
    }
    this.occured_at = occured_at || DateTime.now()
    this.uuid = uuid || uuidv4()
    this.id = id || null
    this.task_id = task_id
    this.task_label = task_label || ''
    this.user_id = user_id || null
    this.version_id = version_id || null
    this.project_id = project_id || null
    this.employee_id = employee_id || null
    this.project_date_id = project_date_id || null
    this.transmitted = transmitted || false
    this.duration_seconds = duration_seconds || 0
    this.is_workplan_event = is_workplan_event || null
    this.is_deleted = is_deleted || false
    this.site = site || null
    this.time_recording_action = time_recording_action
  }
  getPayload(): TEventPayload {
    return {
      id: this.id as number,
      uuid: this.uuid,
      occured_at: this.occured_at
        .set({ millisecond: 0 })
        .toUTC()
        .toISO({ suppressMilliseconds: true }),
      task_id: this.task_id,
      project_id: this.project_id,
      task_label: this.task_label,
      employee_id: this.employee_id,
      is_workplan_event: this.is_workplan_event,
      site_id: this.site?.id || null,
      time_recording_action: this.time_recording_action,
    }
  }
  toJSON(): object {
    return {
      id: this.id,
      uuid: this.uuid,
      occured_at: this.occured_at.toISO(),
      task_id: this.task_id,
      project_id: this.project_id,
      employee_id: this.employee_id,
      project_date_id: this.project_date_id,
      duration_seconds: this.duration_seconds,
      transmitted: this.transmitted,
      is_workplan_event: this.is_workplan_event,
      task_label: this.task_label,
      time_recording_action: this.time_recording_action,
    }
  }
  canBeIgnored(): boolean {
    return !!(
      [
        Task.id._ABSENT_DURING_CORE_TIME,
        Task.id._NOT_RATED,
        Task.id._LOGOUT_DURING_CORE_TIME,
      ].includes(this.task_id) ||
      (this.task_id === Task.id.BREAK && this.is_workplan_event)
    )
  }

  isWorkEvent(): boolean {
    return ![
      Task.id._NOT_RATED,
      Task.id.BREAK,
      Task.id.CLOCKOUT,
      Task.id._LOGOUT_DURING_CORE_TIME,
      Task.id._ABSENT_DURING_CORE_TIME,
    ].includes(this.task_id)
  }

  static fromJSON(dhEvent: TDehydratedEvent): Event {
    return new Event(
      dhEvent.task_id,
      dhEvent.project_id,
      dhEvent.employee_id,
      dhEvent.project_date_id,
      DateTime.fromISO(dhEvent.occured_at),
      dhEvent.uuid,
      dhEvent.id,
      dhEvent.task_label,
      dhEvent.user_id,
      dhEvent.transmitted,
      dhEvent.duration_seconds,
      dhEvent.version_id,
      dhEvent.is_workplan_event,
      dhEvent.is_deleted || false,
      dhEvent?.site,
    )
  }

  static fromServerObject(serverEvent: TServerEvent): Event {
    const now = DateTime.now().minus(Duration.fromMillis(1000)) //now plus 1 sec
    const occured = DateTime.fromISO(serverEvent.occured_at)
    //if occured is younger than now, we set occured_at to now
    // -> no minus time possible!
    const occuredData = occured > now ? now : occured
    return new Event(
      serverEvent.task_id,
      serverEvent.project_id,
      serverEvent.employee_id,
      serverEvent.project_date_id,
      occuredData,
      serverEvent.uuid,
      serverEvent.id,
      serverEvent.task_label,
      null,
      true,
      0,
      serverEvent.version_id,
      serverEvent.is_workplan_event,
      serverEvent.is_deleted || false,
      serverEvent.site,
    )
  }
}

export { Event, type TDehydratedEvent }
